import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import { ExtractionField } from '../pages/extraction-page/extraction-page.component';
import { ExtractionPage, ExtractionSection } from '../models/domain-extraction-specifications';
import { SimpleFormModel } from '@ps-gui/ui';
import { FieldGroupModel } from '../models/extraction-models/extraction-models';
import { FormControlStatus } from '@angular/forms';
import { associate } from '@ps-gui/shared-utils';
import { WorkflowService } from './app-state/workflow.service';
import { mapToFormModel } from '../domains/data-capturing/field-group-extraction-view-component/form-model.mapper';
import { AppView, isConsolidationView, isErfasseBehandlerView } from '../models/app-view';
import { CtBatchStoreService } from './app-state/ct-batch-store.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CurrentFieldService {
  private readonly workflowService = inject(WorkflowService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);

  private readonly _extractionPage = signal<ExtractionPage | undefined>(undefined);
  private readonly _currentView = signal<AppView | undefined>(undefined);

  readonly behandlerErfassungComplete = new Subject<void>();

  get extractionPage() {
    return this._extractionPage();
  }

  set extractionPage(page: ExtractionPage | undefined) {
    this._extractionPage.set(page);
  }

  set currentView(view: AppView | undefined) {
    this._currentView.set(view);
  }

  private readonly _currentActiveField = linkedSignal<ExtractionField | undefined>(() => {
    const page = this._extractionPage();
    if (!page || !page.sections || page.sections.length === 0) return undefined;
    const source = this.ctBatchStoreService.ctBatch?.source;

    const view = this._currentView()!;

    for (const section of page.sections) {
      const validFields = section.fieldModels.filter((field) => {
        if (field.onlyVisibleInConsolidation && !isConsolidationView(view)) {
          return false;
        }
        if (!field.visibleForSource) return true;
        return source ? field.visibleForSource.includes(source) : true;
      });

      if (validFields.length > 0) {
        const firstField = validFields[0];
        return {
          model: firstField,
        };
      }
    }

    return undefined;
  });

  private readonly currentFieldGroupFieldModels = computed(
    () =>
      this._currentFieldGroupModel()?.fieldModels.filter((field) => {
        const currentView = this._currentView();
        if (!currentView) {
          return !field.onlyVisibleInConsolidation;
        }

        return !field.onlyVisibleInConsolidation || isConsolidationView(currentView);
      }) ?? [],
  );

  get currentActiveField() {
    return this._currentActiveField();
  }

  set currentActiveField(field: ExtractionField | undefined) {
    this._currentActiveField.set(field);
  }

  private readonly _currentActiveSection = linkedSignal<ExtractionSection | undefined>(
    () => this._extractionPage()?.sections[0] ?? undefined,
  );

  get currentActiveSection() {
    return this._currentActiveSection();
  }

  set currentActiveSection(section: ExtractionSection | undefined) {
    this._currentActiveSection.set(section);
  }

  private readonly _currentActiveFieldModel = computed(() => this._currentActiveField()?.model);

  get currentActiveFieldModel() {
    return this._currentActiveFieldModel();
  }

  private readonly _currentFieldGroupModel = computed(
    () =>
      this._extractionPage()?.sections.find((section) =>
        section.fieldModels
          .map((model) => model.key)
          .includes(this._currentActiveFieldModel()?.key ?? ''),
      ) as FieldGroupModel | undefined,
  );

  // One physical Enter is delivered synchronously to every live subscriber of the
  // shared `Keys.Enter` subject. When several field-extraction overlays are visible
  // at once (e.g. the consolidation preview or "show all documents"), each overlay
  // calls focusNextField(), advancing the shared active field once per overlay. This
  // guard collapses that synchronous burst to a single advance and resets via the
  // setTimeout callback, so a subsequent Enter still advances.
  private isAdvancing = false;

  focusNextField() {
    if (this.isAdvancing) return;
    this.isAdvancing = true;
    setTimeout(() => (this.isAdvancing = false));

    const activeModel = this._currentActiveFieldModel();
    const fieldModels = this.currentFieldGroupFieldModels();

    const currentIndex = fieldModels.findIndex((field) => field.key === activeModel?.key);

    let nextValidIndex = -1;
    let nextValidFieldModel = undefined;

    for (let i = currentIndex + 1; i < fieldModels.length; i++) {
      // very ugly - but unfortunately we need this...
      if (
        !fieldModels[i].disabled && fieldModels[i].visibleForSource
          ? fieldModels[i].visibleForSource?.includes(this.ctBatchStoreService.ctBatch?.source)
          : true
      ) {
        nextValidIndex = i;
        nextValidFieldModel = fieldModels[i];
        break;
      }
    }

    // if it is the last cell in the table row - the nextValidFieldModel is
    // undefined because we don't know the model of the next field
    // but the table itself adds a new empty row then - so we let the
    // table magic handle the next focus
    // TODO maybe we can solve this in a cleaner way?
    if (
      activeModel?.key !== 'forderungen_betrag' &&
      activeModel?.key !== 'operationen_und_prozeduren_leistungsdatum' &&
      activeModel?.key !== 'diagnosen_lokalisation'
    ) {
      if (nextValidFieldModel && nextValidIndex !== -1) {
        this._currentActiveField.set({
          model: nextValidFieldModel!,
          control: (
            mapToFormModel({
              ...this._currentFieldGroupModel()!,
              fieldModels,
            }) as SimpleFormModel
          ).fieldModels[nextValidIndex].control!,
        });
      } else {
        this.focusNextSection();
      }
    }
  }

  protected readonly statusBySectionKey = linkedSignal<
    Record<string, FormControlStatus | undefined>
  >(() =>
    associate(
      this._extractionPage()?.sections ?? [],
      (section) => section.key,
      () => 'DISABLED',
    ),
  );

  updateSectionStatus(section: ExtractionSection, status: FormControlStatus) {
    this.statusBySectionKey.update((value) => ({ ...value, [section.key]: status }));
  }

  private readonly _areAllSectionsValid = computed(() =>
    Object.values(this.statusBySectionKey()).every(
      (status) => status === 'VALID' || status === 'DISABLED',
    ),
  );

  get areAllSectionsValid() {
    return this._areAllSectionsValid();
  }

  focusNextSection() {
    const currentSection = this._currentActiveSection();
    const sections = this._extractionPage()?.sections;

    if (!currentSection || !sections) return;
    const nextSection = sections
      .filter((sec) => sec.index > currentSection.index)
      .sort((a, b) => a.index - b.index)[0];

    if (!nextSection) {
      if (this._areAllSectionsValid()) {
        if (isErfasseBehandlerView(this.workflowService.currentView())) {
          this.behandlerErfassungComplete.next();
        } else {
          this.workflowService.navigateToNextView().then();
        }
      }
      return;
    }

    this.focusSection(nextSection);
  }

  private focusSection(section: ExtractionSection) {
    this._currentActiveField.set({
      model: section.fieldModels[0],
    });
  }
}
